// pages/UserManagement.js - Halaman manajemen user untuk admin (role list dinamis dari /api/roles)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  Users, Plus, Edit2, Trash2, Key, Save, X,
  CheckCircle2, AlertTriangle, Shield, Eye, EyeOff,
  RefreshCw, Search, UserCheck, Clock, Copy,
} from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import CardList from '../components/mobile/CardList';
import DetailSheet from '../components/mobile/DetailSheet';

// Style badge per role - role bawaan dapat warna khusus, role custom default slate
const ROLE_STYLES = {
  admin: { badge: 'bg-blue-100 text-blue-700',       icon: Shield },
  SAP:   { badge: 'bg-emerald-100 text-emerald-700', icon: UserCheck },
  AWS:   { badge: 'bg-amber-100 text-amber-700',     icon: UserCheck },
};
const DEFAULT_ROLE_STYLE = { badge: 'bg-slate-100 text-slate-700', icon: UserCheck };

// Label permission untuk display
const PERMISSION_LABELS = {
  aws_billing:       'AWS Billing',
  aws_cost:          'AWS Cost Explorer',
  solman:            'SAP Solman',
  sap_usage:         'SAP User Usage',
  logs:              'Activity Log',
  users:             'User Management',
  table_maintenance: 'Table Maintenance',
  notes_plain:       'Notes Plain',
  role_management:   'Role Management',
};

function formatDate(str, lang) {
  if (!str) return '—';
  try {
    return new Date(str.replace(' ', 'T') + 'Z').toLocaleString(lang === 'en' ? 'en-US' : 'id-ID', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return str; }
}

function getRoleStyle(role) {
  return ROLE_STYLES[role] || DEFAULT_ROLE_STYLE;
}

// ── Modal: Tambah / Edit User ─────────────────────────────────────────────────
function UserModal({ user, copyFrom, roles, onSave, onClose }) {
  const { t } = useLang();
  const isEdit = !!user;
  const navigate = useNavigate();
  const defaultRole = roles.find(r => r.name === 'SAP')?.name || roles[0]?.name || '';
  const [form, setForm] = useState({
    username: user?.username || '',
    password: '',
    role:     user?.role     || copyFrom?.role || defaultRole,
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const selectedRole = roles.find(r => r.name === form.role);

  const validate = () => {
    if (!form.username.trim()) return t('um.errUserReq');
    if (form.username.length < 3) return t('um.errUserMin');
    if (!isEdit && !form.password.trim()) return t('um.errPwReq');
    if (form.password && form.password.length < 6) return t('um.errPwMin');
    if (!form.role) return t('um.errRoleReq');
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError(null);
    try {
      const payload = { username: form.username.trim(), role: form.role };
      if (form.password) payload.password = form.password;

      if (isEdit) {
        await api.put(`/api/users/${user.id}`, payload);
      } else {
        await api.post('/api/users', payload);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.error || t('um.errSaveUser'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800">{isEdit ? t('um.editUser') : copyFrom ? t('um.copyUser') : t('um.addUser')}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{isEdit ? t('um.editingUser', { name: user.username }) : copyFrom ? t('um.copyingFrom', { name: copyFrom.username }) : t('um.createAccount')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X style={{ width: 16, height: 16 }} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('um.username')}</label>
            <input
              type="text" value={form.username}
              onChange={e => set('username', e.target.value)}
              disabled={isEdit}
              placeholder={t('um.usernamePlaceholder')}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none
                focus:border-brand-400 focus:ring-2 focus:ring-brand-100
                disabled:bg-slate-50 disabled:text-slate-400"
            />
            {isEdit && <p className="text-xs text-slate-400 mt-1">{t('um.usernameLocked')}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              {t('um.password')} {isEdit && <span className="text-slate-400 font-normal">{t('um.pwOptional')}</span>}
            </label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'} autoComplete="new-password" value={form.password}
                onChange={e => set('password', e.target.value)}
                placeholder={isEdit ? t('um.pwNewPlaceholder') : t('um.pwPlaceholder')}
                className="w-full px-4 py-2.5 pr-10 rounded-xl border border-slate-200 text-sm outline-none
                  focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              <button type="button" onClick={() => setShowPass(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPass ? <EyeOff style={{ width: 15, height: 15 }} /> : <Eye style={{ width: 15, height: 15 }} />}
              </button>
            </div>
          </div>

          {/* Role - dropdown dinamis */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('um.role')}</label>
            <div className="grid grid-cols-2 gap-2">
              {roles.map(r => (
                <div key={r.name} className="relative group">
                  <button type="button" onClick={() => set('role', r.name)}
                    className={`w-full py-2 pl-3 pr-8 rounded-xl border text-sm font-semibold transition-all
                      ${form.role === r.name
                        ? 'border-brand-500 bg-brand-600 text-white shadow-md'
                        : 'border-slate-200 text-slate-600 hover:border-brand-300 hover:bg-brand-50'
                      }`}>
                    {r.name}
                  </button>
                  {r.name !== 'admin' && (
                    <button type="button" title={t('rm.editRole')}
                      onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/roles?edit=${encodeURIComponent(r.name)}`); }}
                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-all
                        ${form.role === r.name
                          ? 'text-white/80 hover:text-white hover:bg-white/20'
                          : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                      <Edit2 style={{ width: 13, height: 13 }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {/* Permission preview */}
            {selectedRole && (
              <div className="mt-2 px-3 py-2 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium mb-1">{t('um.access')}</p>
                <div className="flex flex-wrap gap-1">
                  {selectedRole.name === 'admin' ? (
                    <span className="text-xs bg-brand-50 border border-brand-200 px-2 py-0.5 rounded text-brand-700">{t('um.allAccessAdmin')}</span>
                  ) : selectedRole.permissions.length === 0 ? (
                    <span className="text-xs italic text-slate-400">{t('um.noAccess')}</span>
                  ) : selectedRole.permissions.map(p => (
                    <span key={p} className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-600">
                      {PERMISSION_LABELS[p] || p}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle style={{ width: 14, height: 14 }} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex gap-2">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-50 border border-slate-200 rounded-xl transition-all">
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <RefreshCw style={{ width: 14, height: 14 }} className="animate-spin" /> : <Save style={{ width: 14, height: 14 }} />}
            {isEdit ? t('um.saveChanges') : t('um.createUser')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Reset Password ─────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose }) {
  const { t } = useLang();
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState(null);

  const handleReset = async () => {
    if (!password || password.length < 6) { setError(t('um.errPwMin')); return; }
    setSaving(true); setError(null);
    try {
      await api.put(`/api/users/${user.id}`, { password, role: user.role });
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(e.response?.data?.error || t('um.errReset'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Key style={{ width: 15, height: 15 }} className="text-amber-500" />
              {t('um.resetPw')}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">{t('um.userLabel')} <strong>{user.username}</strong></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X style={{ width: 16, height: 16 }} className="text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {success ? (
            <div className="flex items-center gap-2 py-3 text-green-600">
              <CheckCircle2 style={{ width: 18, height: 18 }} />
              <span className="font-medium">{t('um.pwResetOk')}</span>
            </div>
          ) : (
            <>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} autoComplete="new-password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('um.pwResetPlaceholder')}
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff style={{ width: 15, height: 15 }} /> : <Eye style={{ width: 15, height: 15 }} />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle style={{ width: 13, height: 13 }} />{error}
                </p>
              )}
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-xl hover:bg-slate-50">{t('common.cancel')}</button>
                <button onClick={handleReset} disabled={saving}
                  className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                  {saving ? <RefreshCw style={{ width: 13, height: 13 }} className="animate-spin" /> : <Key style={{ width: 13, height: 13 }} />}
                  {t('um.resetPw')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main UserManagement ───────────────────────────────────────────────────────
export default function UserManagement() {
  const { t, lang } = useLang();
  const [users, setUsers]           = useState([]);
  const [roles, setRoles]           = useState([]); // ← dinamis dari /api/roles
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [modal, setModal]           = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Buka modal Add User otomatis bila datang dari Role Management (?create=1)
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setModal('add');
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [toast, setToast]           = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const isMobile = useIsMobile();
  const [detailFor, setDetailFor] = useState(null); // user.id → bottom-sheet detail (mobile)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/roles'),
      ]);
      setUsers(usersRes.data.users || []);
      setRoles(rolesRes.data.roles || []);
    } catch (e) {
      showToast(t('um.errLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (user) => {
    try {
      await api.delete(`/api/users/${user.id}`);
      showToast(t('um.userDeleted', { name: user.username }));
      fetchUsers();
    } catch (e) {
      showToast(e.response?.data?.error || t('um.errDelete'), 'error');
    } finally {
      setModal(null);
    }
  };

  const getPermissionsOf = (roleName) => {
    const r = roles.find(x => x.name === roleName);
    if (!r) return [];
    if (r.name === 'admin') return [t('um.allAccess')];
    return r.permissions.map(p => PERMISSION_LABELS[p] || p);
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.username.toLowerCase().includes(search.toLowerCase());
    const matchRole   = !filterRole || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleNames = roles.map(r => r.name);

  return (
    <div className="p-6 content-animate">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.type === 'success'
            ? <CheckCircle2 style={{ width: 16, height: 16 }} />
            : <AlertTriangle style={{ width: 16, height: 16 }} />
          }
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-slate-800 text-xl flex items-center gap-2">
            <Users style={{ width: 22, height: 22 }} className="text-brand-600" />
            {t('um.title')}
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">{t('um.subtitle', { n: users.length, m: roles.length })}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchUsers} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all">
            <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
          <button onClick={() => navigate('/dashboard/roles?create=1')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 text-sm font-semibold rounded-xl transition-all">
            <Shield style={{ width: 15, height: 15 }} />
            {t('rm.createRoleBtn')}
          </button>
          <button onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md">
            <Plus style={{ width: 15, height: 15 }} />
            {t('um.addUserBtn')}
          </button>
        </div>
      </div>

      {/* Stats Cards - dinamis per role */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
          <div className="text-2xl font-bold text-brand-600">{users.length}</div>
          <div className="text-xs text-slate-400 font-medium mt-0.5">{t('um.totalUsers')}</div>
        </div>
        {roles.slice(0, 3).map(r => (
          <div key={r.name} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-slate-700">{users.filter(u => u.role === r.name).length}</div>
            <div className="text-xs text-slate-400 font-medium mt-0.5">{r.name}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search style={{ width: 13, height: 13 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} autoComplete="off" name="userFilter"
            placeholder={t('um.searchPlaceholder')}
            className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand-400" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['', ...roleNames].map(r => (
            <button key={r || 'all'} onClick={() => setFilterRole(r)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all
                ${filterRole === r
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'
                }`}>
              {r || t('um.all')}
            </button>
          ))}
        </div>
      </div>

      {/* Table / mobile cards */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {isMobile ? (
          loading ? (
            <div className="px-4 py-12 text-center text-slate-400">
              <RefreshCw style={{ width: 20, height: 20 }} className="animate-spin mx-auto mb-2" />
              {t('common.loading')}
            </div>
          ) : (
            <CardList
              items={filtered}
              keyOf={(u) => String(u.id)}
              title={(u) => u.username}
              badge={(u) => {
                const st = getRoleStyle(u.role);
                return { label: u.role, tone: st === ROLE_STYLES.admin ? 'blue' : st === ROLE_STYLES.SAP ? 'emerald' : st === ROLE_STYLES.AWS ? 'amber' : 'slate' };
              }}
              subtitle={(u) => [
                `#${u.id}`,
                u.last_login ? formatDate(u.last_login, lang) : t('um.never'),
              ].join(' · ')}
              onOpen={(u) => setDetailFor(u.id)}
              emptyText={t('um.empty')}
            />
          )
        ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              {[['num','um.colNum'],['username','um.colUsername'],['role','um.colRole'],['access','um.colAccess'],['created','um.colCreated'],['lastlogin','um.colLastLogin'],['action','um.colAction']].map(([hk,h]) => (
                <th key={hk} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                <RefreshCw style={{ width: 20, height: 20 }} className="animate-spin mx-auto mb-2" />
                {t('common.loading')}
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                <Users style={{ width: 32, height: 32 }} className="mx-auto mb-2 opacity-30" />
                {t('um.empty')}
              </td></tr>
            ) : filtered.map((u, i) => {
              const st = getRoleStyle(u.role);
              const Icon = st.icon;
              const perms = getPermissionsOf(u.role);
              return (
                <tr key={u.id} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono">{u.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {u.username[0].toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-800">{u.username}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${st.badge}`}>
                      <Icon style={{ width: 11, height: 11 }} />
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {perms.length === 0 ? (
                        <span className="text-xs italic text-slate-300">{t('um.none')}</span>
                      ) : perms.slice(0, 3).map(p => (
                        <span key={p} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{p}</span>
                      ))}
                      {perms.length > 3 && (
                        <span className="text-xs text-slate-400">+{perms.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{formatDate(u.created_at, lang)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock style={{ width: 11, height: 11 }} />
                      {u.last_login ? formatDate(u.last_login, lang) : <span className="text-slate-300">{t('um.never')}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setModal({ edit: u })} title={t('um.tipEdit')}
                        className="p-1.5 hover:bg-brand-50 text-slate-400 hover:text-brand-600 rounded-lg transition-all">
                        <Edit2 style={{ width: 14, height: 14 }} />
                      </button>
                      <button onClick={() => setModal({ copy: u })} title={t('um.tipCopy')}
                        className="p-1.5 hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 rounded-lg transition-all">
                        <Copy style={{ width: 14, height: 14 }} />
                      </button>
                      <button onClick={() => setModal({ reset: u })} title={t('um.tipReset')}
                        className="p-1.5 hover:bg-amber-50 text-slate-400 hover:text-amber-600 rounded-lg transition-all">
                        <Key style={{ width: 14, height: 14 }} />
                      </button>
                      <button onClick={() => setModal({ delete: u })} title={t('um.tipDelete')}
                        className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-all">
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>

      {/* Mobile: bottom-sheet detail user — semua kolom + aksi */}
      {isMobile && detailFor != null && (() => {
        const u = users.find((x) => x.id === detailFor);
        if (!u) return null;
        const st = getRoleStyle(u.role);
        const perms = getPermissionsOf(u.role);
        return (
          <DetailSheet
            open
            title={u.username}
            badge={<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.badge}`}>{u.role}</span>}
            fields={[
              { label: 'ID', value: `#${u.id}` },
              { label: t('um.colRole'), value: u.role },
              { label: t('um.colAccess'), value: perms.length === 0 ? t('um.none') : perms.join(', ') },
              { label: t('um.colCreated'), value: formatDate(u.created_at, lang) },
              { label: t('um.colLastLogin'), value: u.last_login ? formatDate(u.last_login, lang) : t('um.never') },
            ]}
            actions={[
              { key: 'edit',   label: t('um.tipEdit'),   Icon: Edit2,  tone: 'blue',
                onClick: () => { setDetailFor(null); setModal({ edit: u }); } },
              { key: 'copy',   label: t('um.tipCopy'),   Icon: Copy,   tone: 'blue',
                onClick: () => { setDetailFor(null); setModal({ copy: u }); } },
              { key: 'reset',  label: t('um.tipReset'),  Icon: Key,    tone: 'amber',
                onClick: () => { setDetailFor(null); setModal({ reset: u }); } },
              { key: 'delete', label: t('um.tipDelete'), Icon: Trash2, tone: 'rose',
                onClick: () => { setDetailFor(null); setModal({ delete: u }); } },
            ]}
            onClose={() => setDetailFor(null)}
          />
        );
      })()}

      {/* Modals — dirender ke document.body agar overlay full layar (tidak terpotong content-animate) */}
      {modal === 'add' && createPortal(
        <UserModal roles={roles}
          onSave={() => { setModal(null); fetchUsers(); showToast(t('um.createdOk')); }}
          onClose={() => setModal(null)}
        />, document.body
      )}
      {modal?.edit && createPortal(
        <UserModal roles={roles} user={modal.edit}
          onSave={() => { setModal(null); fetchUsers(); showToast(t('um.updatedOk')); }}
          onClose={() => setModal(null)}
        />, document.body
      )}
      {modal?.copy && createPortal(
        <UserModal roles={roles} copyFrom={modal.copy}
          onSave={() => { setModal(null); fetchUsers(); showToast(t('um.createdOk')); }}
          onClose={() => setModal(null)}
        />, document.body
      )}
      {modal?.reset && createPortal(
        <ResetPasswordModal user={modal.reset} onClose={() => { setModal(null); fetchUsers(); }} />, document.body
      )}

      {modal?.delete && createPortal(
        <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Trash2 style={{ width: 18, height: 18 }} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">{t('um.deleteUserTitle')}</h3>
                <p className="text-xs text-slate-400">{t('um.irreversible')}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              {t('um.confirmDeleteUser', { name: modal.delete.username, role: modal.delete.role })}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all">
                {t('common.cancel')}
              </button>
              <button onClick={() => handleDelete(modal.delete)}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-all">
                {t('um.yesDelete')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
